import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, PawPrint as Paw, Home as HomeIcon, Info } from 'lucide-react';
import Button from '../../components/UI/Button';
import SEOHead from '../../components/SEO/SEOHead';
import HeroSection from './HeroSection';
import FeaturedDogs from './FeaturedDogs';
import AdoptionSteps from './AdoptionSteps';
import PetHumanConnection from './PetHumanConnection';
import Container from '../../components/UI/Container';
import SectionHeader from '../../components/UI/SectionHeader';
import { publicAPI } from '../../services/api';
const Home: React.FC = () => {
  const [stats, setStats] = useState({
    available: 150, // Fallback value
    adopted: 550   // Fallback value
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await publicAPI.getAnimalsStats();
        if (response.success && response.data) {
          setStats({
            available: response.data.available || 150,
            adopted: response.data.adopted || 550
          });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Keep fallback values
      }
    };

    fetchStats();
  }, []);

  return (
    <>
      <SEOHead 
        title="Adoptă Câini București - Adăpost Protecția Animalelor Sector 1"
        description="Adoptă un câine de la Adăpostul Odăi, Sector 1 București! Câini vaccinați, microcipați și deparazitați căutând o familie iubitoare. Adopție 100% GRATUITĂ! Peste 550 de câini disponibili pentru adopție."
        keywords="adopta caine, adopta caini, adopta caini bucuresti, adaspost caini bucuresti, adopta caine sector 1, adaspost odai, protectia animalelor sector 1, caini bucuresti, adopta catel, caini abandonati, animale adopție bucurești, câini gratis bucuresti, adăpost câini, caini pentru adopție"
        url="/"
        type="website"
        localBusiness={true}
        image="/hero-dogs.jpg"
      />
      <HeroSection />

      <Container>
        <SectionHeader 
          title="Uite cei mai populari prieteni blănoși" 
          subtitle="Aceștia sunt câțiva dintre câinii noștri care așteaptă o familie iubitoare. Tot ce le mai lipsește este dragostea unei familii."
          highlight="prieteni blănoși"
        />

        {/* Statistics Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 mb-12"
        >
          <div className="flex items-center justify-center gap-16">
            {/* Dogs Looking for Home */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Heart className="w-4 h-4 text-accent" />
                <span className="text-2xl font-bold text-primary">{stats.available}</span>
              </div>
              <p className="text-sm text-gray-600">caută o familie</p>
            </div>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-300"></div>

            {/* Dogs Already Adopted */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <HomeIcon className="w-4 h-4 text-primary" />
                <span className="text-2xl font-bold text-accent">{stats.adopted}</span>
              </div>
              <p className="text-sm text-gray-600">au găsit o familie</p>
            </div>
          </div>
        </motion.div>

        <FeaturedDogs />

        <div className="text-center mt-8">
          <Button href="/caini" variant="outline">
            Vezi toți câinii pentru adopție <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </Container>

      <div className="bg-primary-100 py-16">
        <Container className="py-0">
          <SectionHeader 
            title="Adoptă un cățel de la noi" 
            subtitle="Procesul de adopție este simplu și transparent. Iată pașii pe care trebuie să îi urmezi pentru a oferi un cămin iubitor unui câine din adăpostul nostru."
            highlight="Adoptă"
          />
          <AdoptionSteps />
        </Container>
      </div>

      <Container>
        <SectionHeader 
          title="Conviețuire Pașnică Oameni & Animale" 
          centered={false}
        />
        <PetHumanConnection />
      </Container>

      <div className="bg-primary py-16">
        <Container className="py-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <motion.h2 
                className="text-3xl font-bold text-white mb-4 font-display"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Vrei să susții adăpostul nostru?
              </motion.h2>
              <motion.p 
                className="text-white/90 mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Donațiile tale ne ajută să oferim îngrijire, adăpost, mâncare și tratament medical pentru câinii abandonați. Fiecare contribuție contează și face o diferență reală în viața acestor animale.
              </motion.p>
              <motion.div 
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {/* <Button href="/doneaza" variant="accent">
                  <Heart size={16} className="mr-2" /> Donează acum
                </Button> */}
                <Button href="/contact" variant="white">
                  Contactează-ne
                </Button>
              </motion.div>
            </div>
            <motion.div 
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-accent-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <Paw size={24} className="text-accent" />
                </div>
                <h3 className="text-primary font-bold text-lg mb-2">Adopție</h3>
                <p className="text-gray-600">Oferă un cămin permanent unui cățel abandonat</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-accent-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <HomeIcon size={24} className="text-accent" />
                </div>
                <h3 className="text-primary font-bold text-lg mb-2">Găzduire</h3>
                <p className="text-gray-600">Ajută temporar un cățel până găsește o familie</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-accent-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <Heart size={24} className="text-accent" />
                </div>
                <h3 className="text-primary font-bold text-lg mb-2">Donații</h3>
                <p className="text-gray-600">Susține adăpostul nostru cu o donație financiară</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-accent-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  <Info size={24} className="text-accent" />
                </div>
                <h3 className="text-primary font-bold text-lg mb-2">Educare</h3>
                <p className="text-gray-600">Învață despre îngrijirea responsabilă a animalelor</p>
              </div>
            </motion.div>
          </div>
        </Container>
      </div>

      <Container>
        <SectionHeader 
          title="Întrebări și răspunsuri" 
          subtitle="Găsește răspunsuri la cele mai frecvente întrebări despre procesul de adopție și îngrijirea animalelor."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div 
            className="bg-white p-6 rounded-lg shadow-md"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-xl font-bold text-primary mb-4">FAQ's pentru cei ce vor să adopte</h3>
            <p className="text-gray-600 mb-4">
              În cazul în care vrei să adopți un animal și ai întrebări, aici vei găsi răspunsuri pentru nelămuririle tale.
            </p>
            <Button href="/intrebari" variant="outline">
              Vezi toate întrebările
            </Button>
          </motion.div>
          <motion.div 
            className="bg-white p-6 rounded-lg shadow-md"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="text-xl font-bold text-primary mb-4">FAQ's pentru cine vrea să doneze</h3>
            <p className="text-gray-600 mb-4">
              Este foarte important să susținem cauzele frumoase și în mod special pe cea pentru animale.
            </p>
            <Button href="/intrebari" variant="outline">
              Vezi toate întrebările
            </Button>
          </motion.div>
        </div>
      </Container>
    </>
  );
};

export default Home;